import { ref } from 'vue'

import type { ArticlePageContent } from '@backmarket/http-api/src/api-specs-content/models/article-page-content'
import type {
  BasePageContent,
  Context,
} from '@backmarket/http-api/src/api-specs-content/models/base-page-content'
import { validateBlocks } from '@backmarket/nuxt-layer-cms/validateBlocks'
import { useUserStore } from '@backmarket/nuxt-layer-oauth/useUserStore'
import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'
import { useLogger } from '@backmarket/nuxt-module-logger/useLogger'
import { watchAtMost } from '@vueuse/core'

import { usePageParamsFromRoute } from './usePageParamsFromRoute'

type NotNull<T> = Exclude<T, null | undefined>

// checks if the prop exists and is not null/undefined in an object
// if it's the case, the object is narrowed to the type with the prop required
const hasNotNullProp = <T extends object, Key extends keyof T>(
  obj: T,
  prop: Key,
): obj is T & Required<Record<Key, NotNull<T[Key]>>> => {
  return !!obj && typeof obj === 'object' && prop in obj && !!obj[prop]
}

const CMS_PERMISSION = 'group-customer-content-admin-read'

export function useFetchPageContext({
  content,
}: {
  content: ArticlePageContent | BasePageContent | null
}) {
  const userStore = useUserStore()
  const pathParams = usePageParamsFromRoute()
  const $httpFetch = useHttpFetch()
  const logger = useLogger()

  const context = ref<Context>()

  watchAtMost(
    () => userStore.user.isStaff,
    async () => {
      if (!import.meta.client) return
      if (!userStore.user.isStaff) return
      if (!userStore.user.permissions.includes(CMS_PERMISSION)) return

      try {
        context.value = hasNotNullProp(pathParams, 'pageCategory')
          ? await $httpFetch(
              '/content/admin/page/:pageType/:pageName/:pageCategory',
              { pathParams },
            )
          : await $httpFetch('/content/admin/page/:pageType/:pageName', {
              pathParams,
            })
      } catch (error) {
        logger.error('[CMS CONTEXT FETCH FAILED]', {
          error: error as Error,
          owners: ['bot-squad-content-front'],
        })
      }

      if (!!content && context.value) {
        const filteredContextErrors =
          context.value.errors?.filter(
            (error) => !error.message.match(/AttributeError/i),
          ) || []

        context.value.errors = [
          ...filteredContextErrors,
          ...validateBlocks(content.blocks),
        ]
      }
    },
    { immediate: true, count: 2 },
  )

  return context
}
